import type { LinkProps } from "next/link";

import * as React from "react";
import Link from "next/link";
import { useAppTheme } from "~/common/hooks/dom/use-app-theme";

import { ChatLoader } from "~/chat/components/chat-loader";
import { menuItemContainerStyle, menuItemStyle } from "./menu-item.style";

type MenuItemProps<RouteInferType> = {
  href: LinkProps<RouteInferType>["href"];
  children?: React.ReactNode;
  additionalInfo?: string;
  isActive?: boolean;
  onRemove?: () => void;
  className?: string;
  isDisabled?: boolean;
  icon?: React.ReactNode;
  target?: React.HTMLAttributeAnchorTarget;
  isSingleLine?: boolean;
  isLoading?: boolean;
};

function MenuItem<RouteInferType>({
  href,
  children,
  additionalInfo,
  isActive,
  className,
  icon,
  target,
  isSingleLine,
  isLoading,
  ...rest
}: MenuItemProps<RouteInferType>) {
  const appTheme = useAppTheme();

  return (
    <Link
      href={href}
      {...rest}
      target={target}
      prefetch="auto"
      className={menuItemContainerStyle({
        isActive,
        className,
        theme: appTheme,
      })}
    >
      {!!icon && <span className={menuItemStyle.icon()}>{icon}</span>}
      <div className={menuItemStyle.content()}>
        <span className={menuItemStyle.text({ isSingleLine })}>{children}</span>
        {!!additionalInfo && (
          <span className={menuItemStyle.additionalInfo()}>
            {additionalInfo}
          </span>
        )}
      </div>
      {!!isLoading && (
        <div className={menuItemStyle.loader()}>
          <ChatLoader
            variant="ghost"
            dataTestTag="navigation-loader"
          />
        </div>
      )}
    </Link>
  );
}

export { MenuItem };
